interface ContentEditorProps {
  value: string;
  onChange: (v: string) => void;
  error?: string;
  wordCount: number;
  charCount: number;
}

export default function ContentEditor({ value, onChange, error, wordCount, charCount }: ContentEditorProps) {
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');

    // Normalize line endings, split into paragraphs, rejoin with double newline
    const normalized = pasted
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split(/\n{1,}/)
      .map((p) => p.trimEnd())
      .filter((p) => p.length > 0)
      .join('\n\n');

    const textarea = e.currentTarget;
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const newValue = value.slice(0, start) + normalized + value.slice(end);
    onChange(newValue);

    // Restore cursor position after the pasted content
    requestAnimationFrame(() => {
      textarea.selectionStart = start + normalized.length;
      textarea.selectionEnd = start + normalized.length;
    });
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-zinc-300 mb-2 tracking-wide uppercase" style={{ fontFamily: 'Cinzel, serif' }}>
        Story Content <span className="text-red-500">*</span>
      </label>
      <p className="text-zinc-500 text-xs mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
        Minimum 100 characters. Let your darkness flow — no maximum limit. Paragraphs are automatically double-spaced when pasted.
      </p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={handlePaste}
        placeholder="Begin your tale here... The darkness awaits."
        rows={20}
        className={`w-full bg-zinc-900 border ${error ? 'border-red-500' : 'border-zinc-700'} rounded-lg px-5 py-4 text-white text-base placeholder-zinc-600 focus:outline-none focus:border-red-600 transition-colors resize-y leading-relaxed whitespace-pre-wrap`}
        style={{ fontFamily: 'Crimson Text, serif', fontSize: '16px' }}
      />
      <div className="flex items-center justify-between mt-2">
        {error ? (
          <p className="text-red-400 text-xs flex items-center gap-1">
            <i className="ri-error-warning-line"></i> {error}
          </p>
        ) : (
          <span className="text-zinc-600 text-xs">
            {charCount < 100 ? (
              <span className="text-amber-500">{100 - charCount} more characters needed</span>
            ) : (
              <span className="text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i> Minimum length reached
              </span>
            )}
          </span>
        )}
        <div className="flex items-center gap-4 text-zinc-500 text-xs">
          <span>{wordCount.toLocaleString()} words</span>
          <span>{charCount.toLocaleString()} chars</span>
        </div>
      </div>
    </div>
  );
}
